// src/utils/pdfGenerator.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Generate PDF from HTML element with A4 width and content-based height
 */
const generatePDF = async (element, options = {}) => {
  const {
    unit = "mm",
    scale = 3,
    useCORS = true,
    logging = false,
    margin = 15,
    imageType = "image/jpeg",
    imageQuality = 0.95,
    backgroundColor = "#ffffff",
  } = options;

  try {
    // Wait for all images to load
    const images = element.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      }),
    );

    // Get element dimensions
    const elementWidth = element.scrollWidth;
    const elementHeight = element.scrollHeight;

    // Create canvas with proper scaling
    const canvas = await html2canvas(element, {
      scale: scale,
      useCORS: useCORS,
      logging: logging,
      allowTaint: true,
      backgroundColor: backgroundColor,
      width: elementWidth,
      height: elementHeight,
      windowWidth: elementWidth,
      windowHeight: elementHeight,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.getElementById(
          element.id || "invoice-content",
        );
        if (clonedElement) {
          clonedElement.style.width = elementWidth + "px";
          clonedElement.style.height = elementHeight + "px";
        }
      },
    });

    const imgData = canvas.toDataURL(imageType, imageQuality);

    // Create PDF with A4 width only - height will be dynamic
    const A4_WIDTH_MM = 210;
    const pdfWidth = A4_WIDTH_MM;

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const availableWidth = pdfWidth - margin * 2;
    const ratio = availableWidth / imgWidth;
    const pdfHeight = imgHeight * ratio + margin * 2;

    // Create PDF with calculated dimensions
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: unit,
      format: [pdfWidth, pdfHeight],
      compress: true,
    });

    // Center the content
    const finalWidth = imgWidth * ratio;
    const finalHeight = imgHeight * ratio;
    const x = (pdfWidth - finalWidth) / 2;
    const y = margin;

    pdf.addImage(imgData, "JPEG", x, y, finalWidth, finalHeight);

    return pdf;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Download PDF from HTML element
 */
const downloadPDF = async (element, options = {}) => {
  try {
    const { filename = "document.pdf", margin = 15, scale = 3 } = options;

    const pdf = await generatePDF(element, {
      margin,
      scale,
      ...options,
    });

    pdf.save(filename);
    return { success: true, filename };
  } catch (error) {
    console.error("Error downloading PDF:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Print HTML element with proper A4 formatting
 */
const printElement = (element) => {
  if (!element) {
    console.error("No element provided for printing");
    return;
  }

  const printWindow = window.open("", "_blank", "width=800,height=600");
  if (!printWindow) {
    alert("Please allow popups for this site to print.");
    return;
  }

  const styles = document.querySelectorAll('link[rel="stylesheet"], style');
  let stylesHTML = "";
  styles.forEach((style) => {
    if (style.tagName === "LINK") {
      stylesHTML += `<link rel="stylesheet" href="${style.href}">`;
    } else {
      stylesHTML += style.outerHTML;
    }
  });

  const content = element.innerHTML;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        ${stylesHTML}
        <style>
          body { 
            padding: 0; 
            margin: 0; 
            font-family: Arial, sans-serif;
            background: #ffffff;
            color: #000000;
          }
          .no-print { display: none !important; }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          #invoice-content {
            max-width: 210mm;
            margin: 0 auto;
            padding: 20px;
            width: 100%;
          }
          @media print {
            body { 
              padding: 0; 
              margin: 0;
            }
            .no-print { display: none !important; }
            table {
              page-break-inside: avoid;
            }
            tr {
              page-break-inside: avoid;
            }
          }
          @page {
            size: A4;
            margin: 0;
          }
        </style>
      </head>
      <body>
        ${content}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          };
        <\/script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

// ============================================
// ✅ SINGLE EXPORT - NO DUPLICATES
// ============================================

// Export all functions as named exports
export { generatePDF, downloadPDF, printElement };

// Default export object
export default {
  generatePDF,
  downloadPDF,
  printElement,
};
